import { useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import MainHeading from "../components/main-heading.component";
import { COLORS } from "../theme/theme.config";

type RecentTopicStatus = "modified" | "copied" | "unused";

type RecentTopic = {
  title: string;
  hook: string;
  creatives: number;
  activity: string;
  status: RecentTopicStatus;
};

type RecentTopicGroup = {
  day: string;
  count: number;
  topics: RecentTopic[];
};

const recentTopicGroups: RecentTopicGroup[] = [
  {
    day: "Today",
    count: 4,
    topics: [
      {
        status: "modified",
        title: "Why Promotions Are Slowing Down",
        hook: "Most people think promotions are earned. In reality, they're negotiated.",
        creatives: 4,
        activity: "Viewed 2 hours ago",
      },
      {
        status: "copied",
        title: "AI Agents Will Replace Managers",
        hook: "The first managers AI replaces won't be the weakest ones.",
        creatives: 4,
        activity: "Copied today",
      },
    ],
  },
  {
    day: "Yesterday",
    count: 6,
    topics: [
      {
        status: "modified",
        title: "Salary Growth Myths Nobody Talks About",
        hook: "Working harder rarely doubles your income. Learning leverage does.",
        creatives: 8,
        activity: "Modified yesterday",
      },
      {
        status: "unused",
        title: "Networking Secrets For Developers",
        hook: "Your next job probably won't come from a job board.",
        creatives: 4,
        activity: "Generated yesterday",
      },
      {
        status: "copied",
        title: "High Income Skills For 2026",
        hook: "The highest paid skill in 2026 may not exist today.",
        creatives: 4,
        activity: "Copied yesterday",
      },
    ],
  },
];

const recentStats = {
  topics: 127,
  creatives: 642,
};

const surfaceSx = {
  backgroundColor: COLORS.white,
  border: "1px solid rgba(1, 6, 36, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 3px rgba(1, 6, 36, 0.06)",
};

const statusStyles: Record<RecentTopicStatus, { label: string; color: string; bg: string }> = {
  modified: {
    label: "MODIFIED",
    color: COLORS.green,
    bg: "rgba(74, 183, 73, 0.12)",
  },
  copied: {
    label: "COPIED",
    color: COLORS.secondary,
    bg: "rgba(0, 100, 148, 0.1)",
  },
  unused: {
    label: "UNUSED",
    color: COLORS.iconColor,
    bg: "rgba(98, 104, 118, 0.12)",
  },
};

const filterGroups = (groups: RecentTopicGroup[], search: string) => {
  const term = search.trim().toLowerCase();
  if (!term) return groups;

  return groups
    .map((group) => ({
      ...group,
      topics: group.topics.filter((topic) =>
        `${topic.title} ${topic.hook} ${topic.activity} ${topic.status}`.toLowerCase().includes(term)
      ),
    }))
    .filter((group) => group.topics.length > 0);
};

const RecentTopicCard = ({ topic }: { topic: RecentTopic }) => {
  const status = statusStyles[topic.status];

  return (
    <Paper
      sx={{
        ...surfaceSx,
        p: 2.25,
        cursor: "pointer",
        transition: "border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",
        "&:hover": {
          borderColor: COLORS.green,
          transform: "translateY(-3px)",
          backgroundColor: "#f6f8fc",
          boxShadow: "0 8px 20px rgba(1, 6, 36, 0.08)",
        },
      }}
    >
      <Chip
        label={status.label}
        size="small"
        sx={{
          height: 24,
          borderRadius: 1,
          bgcolor: status.bg,
          color: status.color,
          fontSize: 11,
          fontWeight: 800,
          mb: 1.75,
        }}
      />

      <Typography sx={{ color: COLORS.primary, fontSize: 18, fontWeight: 800, lineHeight: 1.35, mb: 1.5 }}>
        {topic.title}
      </Typography>

      <Box
        sx={{
          bgcolor: "rgba(74, 183, 73, 0.08)",
          borderRadius: 2,
          p: 1.5,
          mb: 1.75,
        }}
      >
        <Typography sx={{ color: COLORS.iconColor, fontSize: 13, lineHeight: 1.5 }}>"{topic.hook}"</Typography>
      </Box>

      <Typography sx={{ color: COLORS.iconColor, fontSize: 13, mb: 0.5 }}>
        {topic.creatives} creatives
      </Typography>
      <Typography sx={{ color: COLORS.iconColor, fontSize: 13 }}>{topic.activity}</Typography>
    </Paper>
  );
};

const LibraryRecent = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const filteredGroups = useMemo(() => filterGroups(recentTopicGroups, search), [search]);

  return (
    <div className="container-fluid">
      <MainHeading
        title="Recent Topics"
        subTitle="Continue creating from your latest ideas and revisit previous content."
      />

      <Box sx={{ width: "100%", maxWidth: 900, pb: 4 }}>
        <Paper sx={{ ...surfaceSx, p: { xs: 2.25, md: 3 } }}>
          <Box
            component="button"
            onClick={() => navigate("/library")}
            sx={{
              appearance: "none",
              border: 0,
              background: "transparent",
              color: COLORS.iconColor,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              fontSize: 14,
              fontWeight: 700,
              p: 0,
              mb: 2.5,
              "&:hover": { color: COLORS.green },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Back to Library
          </Box>

          <Typography sx={{ color: COLORS.green, fontSize: 14, fontWeight: 800, mb: 3 }}>
            {recentStats.topics} Topics | {recentStats.creatives} Creatives Generated
          </Typography>

          <TextField
            fullWidth
            autoComplete="off"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setSearch("");
            }}
            placeholder="Search recent topics..."
            variant="outlined"
            size="small"
            sx={{
              mb: 4,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: "#f6f8fc",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: COLORS.iconColor }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0.4, color: COLORS.iconColor, "&:hover": { color: COLORS.primary } }}>
                    <CloseIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />

          <Box sx={{ display: "grid", gap: 4 }}>
            {filteredGroups.map((group) => (
              <Box key={group.day}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Typography sx={{ color: COLORS.primary, fontSize: 20, fontWeight: 800 }}>
                    {group.day}
                  </Typography>
                  <Typography sx={{ color: COLORS.iconColor, fontSize: 13, whiteSpace: "nowrap" }}>
                    {group.count} Topics
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                    gap: 2.25,
                  }}
                >
                  {group.topics.map((topic) => (
                    <RecentTopicCard key={topic.title} topic={topic} />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          {filteredGroups.length === 0 && (
            <Paper sx={{ p: 3, textAlign: "center", bgcolor: "#f6f8fc", borderRadius: 2 }}>
              <Typography sx={{ color: COLORS.iconColor }}>No recent topics match your search.</Typography>
            </Paper>
          )}

          <Paper
            onClick={() => navigate("/library/history")}
            sx={{
              mt: 3.5,
              p: 2.25,
              border: "1px solid rgba(1, 6, 36, 0.08)",
              borderRadius: 2,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 2,
              boxShadow: "none",
              transition: "border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": {
                borderColor: COLORS.green,
                transform: "translateY(-2px)",
                boxShadow: "0 6px 18px rgba(1, 6, 36, 0.08)",
              },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: "rgba(0, 100, 148, 0.08)",
                color: COLORS.secondary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <MenuBookOutlinedIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: COLORS.primary, fontWeight: 800, mb: 0.5 }}>
                Browse Full History
              </Typography>
              <Typography sx={{ color: COLORS.iconColor, fontSize: 14 }}>
                Search across all your generated topics and creatives
              </Typography>
            </Box>
            <ArrowForwardIcon sx={{ color: COLORS.green }} />
          </Paper>
        </Paper>
      </Box>
    </div>
  );
};

export default LibraryRecent;
